/**
 * Permission Testing Modal Component
 * Epic #17: RBAC Permission Testing & Simulation Tools
 *
 * Modal for testing permission scenarios without applying real changes
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  TextInput,
  Select,
  SelectItem,
  Dropdown,
  DatePicker,
  DatePickerInput,
  RadioButton,
  RadioButtonGroup,
  Tile,
  Tag,
  InlineNotification,
  Loading,
  SkeletonText,
  Stack,
} from '@carbon/react';
import { CheckmarkFilled, CloseFilled, Information } from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { ResourceType } from '../../../../shared/interfaces/rbac.interface';
import type { PermissionTestRequest, PermissionTestResponse } from '../../../../shared/interfaces/rbac.interface';
import styles from './PermissionTestingModal.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface PermissionTestingModalProps {
  open: boolean;
  onClose: () => void;
}

const PermissionTestingModal: React.FC<PermissionTestingModalProps> = ({ open, onClose }) => {
  // Get token from localStorage directly (consistent with RBACDashboard pattern)
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';

  // Form state
  const [simulationMode, setSimulationMode] = useState<'current' | 'hypothetical' | 'historical' | 'diff'>('current');
  const [userId, setUserId] = useState('');
  const [resourceType, setResourceType] = useState<ResourceType>(ResourceType.CURRICULUM);
  const [resourceId, setResourceId] = useState('');
  const [action, setAction] = useState('read');
  const [hypotheticalRoleId, setHypotheticalRoleId] = useState('');
  const [historicalDate, setHistoricalDate] = useState('');
  const [diffRoleId, setDiffRoleId] = useState('');
  const [dryRun, setDryRun] = useState(true);

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PermissionTestResponse | null>(null);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  // Fetch available roles
  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/rbac/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAvailableRoles(response.data || []);
    } catch (err: any) {
      console.error('Failed to fetch roles:', err);
    }
  };

  // Fetch available users (for demo purposes, could be a search endpoint)
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/admin/users?limit=50`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAvailableUsers(response.data.users || []);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRoles();
      fetchUsers();
    }
  }, [open]);

  // Handle test submission
  const handleTest = async () => {
    if (!userId || !resourceType || !resourceId || !action) {
      setError('Please fill in all required fields');
      return;
    }

    // Validate mode-specific fields
    if (simulationMode === 'hypothetical' && !hypotheticalRoleId) {
      setError('Hypothetical role is required for hypothetical mode');
      return;
    }
    if (simulationMode === 'historical' && !historicalDate) {
      setError('Historical date is required for historical mode');
      return;
    }
    if (simulationMode === 'diff' && !diffRoleId) {
      setError('Diff role is required for diff mode');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const requestData: PermissionTestRequest = {
        simulation_mode: simulationMode,
        user_id: userId,
        resource_type: resourceType,
        resource_id: resourceId,
        action,
        dry_run: dryRun,
      };

      // Add mode-specific fields
      if (simulationMode === 'hypothetical') {
        requestData.hypothetical_role_id = hypotheticalRoleId;
      }
      if (simulationMode === 'historical') {
        requestData.historical_date = historicalDate;
      }
      if (simulationMode === 'diff') {
        requestData.diff_role_id = diffRoleId;
      }

      const response = await axios.post(`${API_BASE_URL}/v1/admin/rbac/test-permission`, requestData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setResult(response.data);
        setError(null);
      } else {
        setError(response.data.message || 'Permission test failed');
      }
    } catch (err: any) {
      console.error('Permission test failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to test permission');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleClose = () => {
    setSimulationMode('current');
    setUserId('');
    setResourceType(ResourceType.CURRICULUM);
    setResourceId('');
    setAction('read');
    setHypotheticalRoleId('');
    setHistoricalDate('');
    setDiffRoleId('');
    setDryRun(true);
    setError(null);
    setResult(null);
    onClose();
  };

  return (
    <ComposedModal open={open} onClose={handleClose} size="lg">
      <ModalHeader>
        <h3>Permission Testing Tool</h3>
        <p className={styles.modalSubtitle}>Test permission scenarios without applying real changes</p>
      </ModalHeader>

      <ModalBody>
        <Form>
          <Stack gap={6}>
            {/* Simulation Mode */}
            <FormGroup legendText="Simulation Mode">
              <RadioButtonGroup
                name="simulation-mode"
                valueSelected={simulationMode}
                onChange={(value: string) => setSimulationMode(value as any)}
                orientation="vertical"
              >
                <RadioButton labelText="Current - Test user's current permissions" value="current" id="mode-current" />
                <RadioButton
                  labelText="Hypothetical - Test user with a specific role (without assigning)"
                  value="hypothetical"
                  id="mode-hypothetical"
                />
                <RadioButton
                  labelText="Historical - Test permissions as of a specific date"
                  value="historical"
                  id="mode-historical"
                />
                <RadioButton
                  labelText="Diff - Show what would change if role is assigned"
                  value="diff"
                  id="mode-diff"
                />
              </RadioButtonGroup>
            </FormGroup>

            {/* User Selection */}
            <FormGroup legendText="User">
              <Select id="user-select" labelText="Select User" value={userId} onChange={e => setUserId(e.target.value)}>
                <SelectItem value="" text="Select a user..." />
                {availableUsers.map(user => (
                  <SelectItem key={user.user_id} value={user.user_id} text={`${user.email} (${user.role})`} />
                ))}
              </Select>
            </FormGroup>

            {/* Resource Type */}
            <FormGroup legendText="Resource Type">
              <Select
                id="resource-type-select"
                labelText="Select Resource Type"
                value={resourceType}
                onChange={e => setResourceType(e.target.value as ResourceType)}
              >
                <SelectItem value={ResourceType.CURRICULUM} text="Curriculum" />
                <SelectItem value={ResourceType.ASSESSMENT} text="Assessment" />
                <SelectItem value={ResourceType.STUDENT_DATA} text="Student Data" />
                <SelectItem value={ResourceType.ANALYTICS} text="Analytics" />
                <SelectItem value={ResourceType.CONTENT} text="Content" />
                <SelectItem value={ResourceType.ADMINISTRATION} text="Administration" />
                <SelectItem value={ResourceType.SYSTEM_CONFIG} text="System Configuration" />
              </Select>
            </FormGroup>

            {/* Resource ID */}
            <FormGroup legendText="Resource ID">
              <TextInput
                id="resource-id"
                labelText="Resource ID"
                placeholder="e.g., course-123, assessment-456"
                value={resourceId}
                onChange={e => setResourceId(e.target.value)}
              />
            </FormGroup>

            {/* Action */}
            <FormGroup legendText="Action">
              <Select
                id="action-select"
                labelText="Select Action"
                value={action}
                onChange={e => setAction(e.target.value)}
              >
                <SelectItem value="read" text="Read" />
                <SelectItem value="write" text="Write" />
                <SelectItem value="delete" text="Delete" />
                <SelectItem value="manage" text="Manage" />
                <SelectItem value="grade" text="Grade" />
                <SelectItem value="view" text="View" />
              </Select>
            </FormGroup>

            {/* Mode-specific fields */}
            {simulationMode === 'hypothetical' && (
              <FormGroup legendText="Hypothetical Role">
                <Select
                  id="hypothetical-role-select"
                  labelText="Select Role to Test"
                  value={hypotheticalRoleId}
                  onChange={e => setHypotheticalRoleId(e.target.value)}
                >
                  <SelectItem value="" text="Select a role..." />
                  {availableRoles.map(role => (
                    <SelectItem key={role.role_id} value={role.role_id} text={role.role_name} />
                  ))}
                </Select>
              </FormGroup>
            )}

            {simulationMode === 'historical' && (
              <FormGroup legendText="Historical Date">
                <TextInput
                  id="historical-date"
                  labelText="Test Date (ISO format)"
                  placeholder="e.g., 2025-01-01T00:00:00Z"
                  value={historicalDate}
                  onChange={e => setHistoricalDate(e.target.value)}
                  helperText="Enter date in ISO 8601 format"
                />
              </FormGroup>
            )}

            {simulationMode === 'diff' && (
              <FormGroup legendText="Diff Role">
                <Select
                  id="diff-role-select"
                  labelText="Select Role to Compare"
                  value={diffRoleId}
                  onChange={e => setDiffRoleId(e.target.value)}
                >
                  <SelectItem value="" text="Select a role..." />
                  {availableRoles.map(role => (
                    <SelectItem key={role.role_id} value={role.role_id} text={role.role_name} />
                  ))}
                </Select>
              </FormGroup>
            )}

            {/* Error Display */}
            {error && (
              <InlineNotification
                kind="error"
                title="Error"
                subtitle={error}
                onCloseButtonClick={() => setError(null)}
              />
            )}

            {/* Result Display */}
            {result && (
              <Tile className={styles.resultTile}>
                <div className={styles.resultHeader}>
                  {result.test_result.allowed ? (
                    <>
                      <CheckmarkFilled size={32} className={styles.successIcon} />
                      <div>
                        <h4 className={styles.resultTitle}>Access Granted</h4>
                        <p className={styles.resultSubtitle}>
                          Permission test completed in {result.test_result.evaluation_time_ms}ms
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <CloseFilled size={32} className={styles.errorIcon} />
                      <div>
                        <h4 className={styles.resultTitle}>Access Denied</h4>
                        <p className={styles.resultSubtitle}>
                          Permission test completed in {result.test_result.evaluation_time_ms}ms
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Decision Basis */}
                <div className={styles.resultSection}>
                  <h5 className={styles.sectionTitle}>Decision Basis</h5>
                  <p className={styles.sectionText}>
                    <strong>Primary Role:</strong> {result.test_result.decision_basis.primary_role}
                  </p>
                  <p className={styles.sectionText}>
                    <strong>Applied Rules:</strong> {result.test_result.decision_basis.applied_rules.join(', ')}
                  </p>
                  <p className={styles.sectionText}>
                    <strong>Cache Hit:</strong> {result.test_result.cache_hit ? 'Yes' : 'No'}
                  </p>
                </div>

                {/* Effective Permissions */}
                <div className={styles.resultSection}>
                  <h5 className={styles.sectionTitle}>
                    Effective Permissions ({result.test_result.decision_basis.effective_permissions.length})
                  </h5>
                  <div className={styles.permissionList}>
                    {result.test_result.decision_basis.effective_permissions.slice(0, 10).map((perm, index) => (
                      <Tag key={index} type="blue" size="sm">
                        {perm}
                      </Tag>
                    ))}
                    {result.test_result.decision_basis.effective_permissions.length > 10 && (
                      <Tag type="gray" size="sm">
                        +{result.test_result.decision_basis.effective_permissions.length - 10} more
                      </Tag>
                    )}
                  </div>
                </div>

                {/* Permission Diff (for diff mode) */}
                {result.permission_diff && (
                  <div className={styles.resultSection}>
                    <h5 className={styles.sectionTitle}>Permission Changes</h5>
                    {result.permission_diff.permissions_added.length > 0 && (
                      <div className={styles.diffSubsection}>
                        <p className={styles.diffSubsectionTitle}>
                          Added ({result.permission_diff.permissions_added.length}):
                        </p>
                        <div className={styles.permissionList}>
                          {result.permission_diff.permissions_added.slice(0, 5).map((perm, index) => (
                            <Tag key={index} type="green" size="sm">
                              + {perm}
                            </Tag>
                          ))}
                          {result.permission_diff.permissions_added.length > 5 && (
                            <Tag type="gray" size="sm">
                              +{result.permission_diff.permissions_added.length - 5} more
                            </Tag>
                          )}
                        </div>
                      </div>
                    )}
                    {result.permission_diff.permissions_removed.length > 0 && (
                      <div className={styles.diffSubsection}>
                        <p className={styles.diffSubsectionTitle}>
                          Removed ({result.permission_diff.permissions_removed.length}):
                        </p>
                        <div className={styles.permissionList}>
                          {result.permission_diff.permissions_removed.slice(0, 5).map((perm, index) => (
                            <Tag key={index} type="red" size="sm">
                              - {perm}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Historical Snapshot */}
                {result.historical_snapshot && (
                  <div className={styles.resultSection}>
                    <h5 className={styles.sectionTitle}>Historical Snapshot</h5>
                    <p className={styles.sectionText}>
                      <strong>Test Date:</strong> {new Date(result.historical_snapshot.test_date).toLocaleString()}
                    </p>
                    <p className={styles.sectionText}>
                      <strong>Active Roles:</strong> {result.historical_snapshot.active_roles.join(', ')}
                    </p>
                    <p className={styles.sectionText}>
                      <strong>Active Permissions:</strong> {result.historical_snapshot.active_permissions.length}
                    </p>
                  </div>
                )}

                {/* Hypothetical Scenario */}
                {result.hypothetical_scenario && (
                  <div className={styles.resultSection}>
                    <h5 className={styles.sectionTitle}>Hypothetical Scenario</h5>
                    <p className={styles.sectionText}>
                      <strong>Simulated Role:</strong> {result.hypothetical_scenario.simulated_role}
                    </p>
                    <p className={styles.sectionText}>
                      <strong>Simulated Permissions:</strong>{' '}
                      {result.hypothetical_scenario.simulated_permissions.length}
                    </p>
                  </div>
                )}
              </Tile>
            )}
          </Stack>
        </Form>
      </ModalBody>

      <ModalFooter>
        <Button kind="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button kind="primary" onClick={handleTest} disabled={loading}>
          {loading ? <Loading small withOverlay={false} /> : 'Test Permission'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default PermissionTestingModal;
